import { create } from "zustand";
import type { ComparisonResult, QueryMode } from "../../types";
import { api, type ComparisonListItem, type RunGroupResult } from "@/api/client";
import { createPoller, type Poller } from "@/lib/poller";

// The single active poller for comparison/run-group polling. Held outside the
// store state (it's not render-relevant) but cancelled via the store so that
// workspace switches and 401s can stop it.
let activePoller: Poller | null = null;

function cancelActivePoller() {
  if (activePoller) {
    activePoller.cancel();
    activePoller = null;
  }
}

// Cancel any in-flight comparison poller. Called on workspace switch / logout
// so polls don't continue against the new workspace header.
export function cancelComparisonPolling() {
  cancelActivePoller();
}

interface ActiveComparison {
  id: number;
  status: "polling" | "completed" | "failed";
  result?: ComparisonResult;
}

interface ActiveRunGroup {
  groupId: string;
  expectedCount: number;
  status: "polling" | "completed";
  completedCount: number;
  results?: RunGroupResult[];
}

interface ComparisonsStore {
  history: ComparisonListItem[];
  loading: boolean;
  error: string | null;
  activeComparison: ActiveComparison | null;
  activeRunGroup: ActiveRunGroup | null;
  batchMessage: string | null;

  fetchHistory: () => Promise<void>;
  runComparison: (params: {
    brand_ids: number[];
    prompt_id: number;
    model: string;
    repeat?: number;
    query_mode?: QueryMode;
  }) => Promise<void>;
  runBatch: (params: {
    brand_ids: number[];
    prompt_ids: number[];
    models: string[];
    query_mode?: QueryMode;
  }) => Promise<void>;
  clearActive: () => void;
  clearBatchMessage: () => void;
}

export const useComparisonsStore = create<ComparisonsStore>((set, get) => ({
  history: [],
  loading: false,
  error: null,
  activeComparison: null,
  activeRunGroup: null,
  batchMessage: null,

  fetchHistory: async () => {
    set({ loading: true });
    try {
      const history = await api.comparisons.list();
      set({ history, loading: false, error: null });
    } catch (err: any) {
      set({ loading: false, error: err?.message || "Failed to load history" });
    }
  },

  runComparison: async (params) => {
    const repeat = params.repeat || 1;

    // Cancel any existing poller so a second run never produces interleaved
    // state updates from a stale poll.
    cancelActivePoller();

    if (repeat > 1) {
      // Multi-run: poll run group
      const response = await api.comparisons.run(params);
      // Discriminate the run-group envelope from a single Comparison. Both have a
      // `run_group` field, so narrow on `comparisons`, which only the envelope has.
      if (!('comparisons' in response)) {
        // Shouldn't happen for repeat > 1, but guard the union.
        throw new Error("Expected a run group response for repeated runs");
      }
      const groupId = response.run_group;
      const expectedCount = response.count;

      set({
        activeRunGroup: { groupId, expectedCount, status: "polling", completedCount: 0 },
        activeComparison: null,
      });

      activePoller = createPoller(
        async () => {
          const results = await api.runGroup.get(groupId);
          const completedCount = results[0]?.run_count ?? 0;

          if (completedCount >= expectedCount) {
            set({ activeRunGroup: { groupId, expectedCount, status: "completed", completedCount, results } });
            return true;
          }
          set(state => ({
            activeRunGroup: state.activeRunGroup
              ? { ...state.activeRunGroup, completedCount }
              : null,
          }));
          return false;
        },
        { intervalMs: 3000, timeoutMs: 360000 },
      );
    } else {
      // Single run: poll comparison
      const response = await api.comparisons.run(params);
      if ('comparisons' in response) {
        // Shouldn't happen for repeat <= 1, but guard the union.
        throw new Error("Expected a single comparison response for a single run");
      }
      const compId = response.id;

      set({
        activeComparison: { id: compId, status: "polling" },
        activeRunGroup: null,
      });

      activePoller = createPoller(
        async () => {
          const result = await api.comparisons.get(compId);
          if (result.status === "completed") {
            set({ activeComparison: { id: compId, status: "completed", result } });
            return true;
          }
          if (result.status === "failed") {
            set({ activeComparison: { id: compId, status: "failed" } });
            return true;
          }
          return false;
        },
        { intervalMs: 2000, timeoutMs: 120000 },
      );
    }
  },

  runBatch: async (params) => {
    await api.comparisons.runBatch(params);
    set({ batchMessage: "Batch evaluations started. Check the Activity tab for results." });
  },

  clearActive: () => {
    cancelActivePoller();
    set({ activeComparison: null, activeRunGroup: null });
  },
  clearBatchMessage: () => set({ batchMessage: null }),
}));
